import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { History } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis, XAxis } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MoodHistoryVisualization = ({ history, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Auto-play through history
  useEffect(() => {
    if (!isPlaying || !history?.length) return;
    
    const interval = setInterval(() => {
      setSelectedIndex(prev => {
        if (prev === null || prev >= history.length - 1) {
          setIsPlaying(false);
          return history.length - 1;
        }
        return prev + 1;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [isPlaying, history]);

  if (!history?.length) return null;

  const selectedMood = selectedIndex !== null ? history[selectedIndex] : history[history.length - 1];
  const chartData = history.map((item, idx) => ({
    ...item,
    index: idx,
    value: item.intensity * 100,
    time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  }));

  const handlePlayPause = () => {
    if (!isPlaying) {
      setSelectedIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="apple-card border-0 max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto modal-mobile-optimized modal-glow">
        <DialogHeader>
          <DialogTitle className="apple-headline text-lg flex items-center gap-2">
            <History size={20} />
            {t('moodJourney')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mood-history-viz mt-4">
          {/* Current selected mood display */}
          <motion.div 
            className="mood-history-current"
            animate={{ backgroundColor: `${selectedMood?.color}15` }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="mood-history-orb"
              style={{ backgroundColor: selectedMood?.color }}
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.6, 0.9, 0.6],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <div className="mood-history-info">
              <motion.h2 
                key={selectedMood?.adjective}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mood-history-adjective"
                style={{ color: selectedMood?.color }}
              >
                {selectedMood?.adjective?.toUpperCase()}
              </motion.h2>
              <p className="mood-history-time">
                {selectedMood && new Date(selectedMood.timestamp).toLocaleString()}
              </p>
              <div className="mood-history-stats">
                <span>{t('intensity')}: {Math.round((selectedMood?.intensity || 0) * 100)}%</span>
                <span>•</span>
                <span>{selectedMood?.headlines_analyzed || 0} {t('headlines')}</span>
              </div>
            </div>
          </motion.div>

          {/* Color timeline */}
          <div className="mood-history-timeline">
            <div className="mood-history-colors">
              {history.map((item, idx) => (
                <motion.button
                  key={idx}
                  className={`mood-history-color-block ${selectedIndex === idx ? 'active' : ''}`}
                  style={{ backgroundColor: item.color }}
                  onClick={() => { setSelectedIndex(idx); setIsPlaying(false); }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                />
              ))}
            </div>
            <div className="mood-history-labels">
              <span>{t('older')}</span>
              <span>{t('recent')}</span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="mood-history-controls">
            <button 
              onClick={handlePlayPause}
              className="apple-button apple-button-primary"
            >
              {isPlaying ? t('pause') : t('playJourney')}
            </button>
            <button 
              onClick={() => { setSelectedIndex(history.length - 1); setIsPlaying(false); }}
              className="apple-button"
            >
              {t('latest')}
            </button>
          </div>

          {/* Intensity chart with colored areas */}
          <div className="mood-history-chart">
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={chartData}>
                <defs>
                  {chartData.map((item, idx) => (
                    <linearGradient key={idx} id={`gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={item.color} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={item.color} stopOpacity={0.1} />
                    </linearGradient>
                  ))}
                </defs>
                <YAxis domain={[0, 100]} hide />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'var(--apple-text-tertiary)' }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload?.[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="chart-tooltip">
                          <p style={{ color: data.color, fontWeight: 600 }}>{data.adjective}</p>
                          <p className="text-xs">{Math.round(data.value)}% intensity</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke={selectedMood?.color || '#0a84ff'}
                  strokeWidth={2}
                  fill={`url(#gradient-${selectedIndex || chartData.length - 1})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { MoodHistoryVisualization };
